import {
  AdminFormResponse,
  AdminFormResponsesListResponse,
  CreateFormQuestionRequest,
  CreateFormResponseRequest,
  FormQuestion,
  FormQuestionResponse,
  FormQuestionsListResponse,
  FormResponse,
  FormResponseAPIResponse,
  FormResponseHistory,
  FormResponseHistoryListResponse,
  FormResponsesListResponse,
  QuestionnaireProgress,
  UpdateFormQuestionRequest,
} from "../types/form";
import httpService from "./httpService";

class FormService {
  private readonly basePath = "/v2/form";

  // ==================== Question CRUD (Admin) ====================

  async listQuestions(params?: { category?: string; subcategory?: string }): Promise<FormQuestion[]> {
    const search = new URLSearchParams();
    if (params?.category) search.set("category", params.category);
    if (params?.subcategory) search.set("subcategory", params.subcategory);
    const suffix = search.toString() ? `?${search.toString()}` : "";
    const res = await httpService.get<FormQuestionsListResponse>(`${this.basePath}/question${suffix}`);
    return res.data.questions || [];
  }

  async getQuestion(id: number): Promise<FormQuestion> {
    const res = await httpService.get<FormQuestionResponse>(`${this.basePath}/question/${id}`);
    return res.data.question;
  }

  async createQuestion(payload: CreateFormQuestionRequest): Promise<FormQuestion> {
    const res = await httpService.post<FormQuestionResponse>(`${this.basePath}/question`, payload);
    return res.data.question;
  }

  async updateQuestion(id: number, payload: UpdateFormQuestionRequest): Promise<FormQuestion> {
    const res = await httpService.put<FormQuestionResponse>(`${this.basePath}/question/${id}`, payload);
    return res.data.question;
  }

  async deleteQuestion(id: number): Promise<void> {
    await httpService.delete(`${this.basePath}/question/${id}`);
  }

  // ==================== Response Methods (User) ====================

  /**
   * Submit or update an answer to a question (upsert behavior)
   */
  async submitResponse(payload: CreateFormResponseRequest): Promise<FormResponse> {
    const res = await httpService.post<FormResponseAPIResponse>(`${this.basePath}/response`, payload);
    return res.data.response;
  }

  /**
   * Get responses for the current user, optionally limited to one question.
   */
  async getMyResponses(params?: { questionId?: number }): Promise<FormResponse[]> {
    const search = new URLSearchParams();
    if (params?.questionId !== undefined) {
      search.set("questionId", String(params.questionId));
    }
    const suffix = search.toString() ? `?${search.toString()}` : "";
    const res = await httpService.get<FormResponsesListResponse>(
      `${this.basePath}/response${suffix}`
    );
    return res.data.responses || [];
  }

  /**
   * Get a specific response by ID
   */
  async getResponse(id: number): Promise<FormResponse> {
    const res = await httpService.get<FormResponseAPIResponse>(`${this.basePath}/response/${id}`);
    return res.data.response;
  }

  // ==================== Admin Methods ====================

  /**
   * List all responses across all users (admin only)
   * Uses GET /v2/form/response/all which returns all responses for admins
   */
  async listAllResponses(): Promise<AdminFormResponse[]> {
    const res = await httpService.get<AdminFormResponsesListResponse>(`${this.basePath}/response/all`);
    return res.data.responses || [];
  }

  /**
   * Get responses for a specific user (admin only)
   * Uses GET /v2/form/response/user/:userId
   */
  async getResponsesForUser(userId: string): Promise<AdminFormResponse[]> {
    const res = await httpService.get<AdminFormResponsesListResponse>(`${this.basePath}/response/user/${userId}`);
    return res.data.responses || [];
  }

  /**
   * Get the recorded answer history for one question/user pair (admin only).
   */
  async getResponseHistory(questionId: number, userId: string): Promise<FormResponseHistory[]> {
    const search = new URLSearchParams({
      questionId: String(questionId),
      userId,
    });
    const res = await httpService.get<FormResponseHistoryListResponse>(
      `${this.basePath}/response/history?${search.toString()}`
    );
    return res.data.responseHistory || [];
  }

  /**
   * Delete a single question response by id (admin only).
   * DELETE /v2/form/response/:id
   * Note: the backend returns 500 (not 404) when the response does not exist,
   * so callers must treat any rejection as a failure for that id and reconcile
   * by refetching the current responses.
   */
  async deleteResponse(id: number): Promise<void> {
    await httpService.delete(`${this.basePath}/response/${id}`);
  }

  // ==================== Questionnaire Helpers ====================

  /**
   * Get questionnaire progress for a specific category/subcategory
   * Returns questions and the user's progress
   */
  async getQuestionnaireProgress(params?: {
    category?: string;
    subcategory?: string;
    excludeSubcategory?: string;
  }): Promise<{ questions: FormQuestion[]; progress: QuestionnaireProgress }> {
    // Fetch questions and responses in parallel
    const [questions, responses] = await Promise.all([
      this.listQuestions({ category: params?.category, subcategory: params?.subcategory }),
      this.getMyResponses(),
    ]);

    // Filter questions (case-insensitive)
    let filteredQuestions = questions;

    // Filter by category if specified (case-insensitive)
    if (params?.category) {
      const categoryLower = params.category.toLowerCase();
      filteredQuestions = filteredQuestions.filter(q => q.category.toLowerCase() === categoryLower);
    }

    // Exclude subcategory if specified (case-insensitive)
    if (params?.excludeSubcategory) {
      const excludeLower = params.excludeSubcategory.toLowerCase();
      filteredQuestions = filteredQuestions.filter(q => q.subcategory.toLowerCase() !== excludeLower);
    }

    // Sort questions by order
    const sortedQuestions = [...filteredQuestions].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    // Build response map (questionId -> answer)
    const responseMap = new Map<number, string>();
    const questionIds = new Set(sortedQuestions.map(q => q.id));

    for (const response of responses) {
      if (questionIds.has(response.questionId)) {
        responseMap.set(response.questionId, response.answer);
      }
    }

    const progress: QuestionnaireProgress = {
      totalQuestions: sortedQuestions.length,
      answeredQuestions: responseMap.size,
      isComplete: sortedQuestions.length > 0 && responseMap.size >= sortedQuestions.length,
      responses: responseMap,
    };

    return { questions: sortedQuestions, progress };
  }

  /**
   * Check if questionnaire is complete for a category
   */
  async isQuestionnaireComplete(params?: {
    category?: string;
    subcategory?: string;
    excludeSubcategory?: string;
  }): Promise<boolean> {
    const { progress } = await this.getQuestionnaireProgress(params);
    return progress.isComplete;
  }
}

export const formService = new FormService();
export default formService;
